import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Header from "../Header";
import Loading from "../Loading";

import { Form, Container } from "react-bootstrap";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WestIcon from "@mui/icons-material/West";
import { DateTimeFormatter, UTCDateTimeFormatter } from "../DateTimeFormatter";

import {
  fetchResultsBySession,
  fetchAnswersBySession,
  fetchSession,
} from "../../Features/Sessions/Actions/SessionsActions";

import { decode } from "html-entities";

export default function Results() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const sessionId = parseInt(id);
  const navigate = useNavigate();

  const { onlyWrongQuestions, session, answers, questions, finished, error } =
    useSelector((store) => store.sessions);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionState, setSessionState] = useState(() => ({
    title: "Default Title",
    localeDate: "Default Subtitle",
    UTCDate: "Default Subtitle",
  }));
  const mounted = useRef(null);

  const getSessionHeaderInfo = (sessionData, finishedDate) => {
    const title =
      sessionData?.schedule?.title ||
      sessionData?.schedule?.referencetitle ||
      "Untitled";
    return {
      title,
      localeDate: finishedDate
        ? `Finished on ${DateTimeFormatter(finishedDate)}`
        : "Undefined",
      UTCDate: finishedDate
        ? `Finished on ${UTCDateTimeFormatter(finishedDate)}`
        : "Undefined",
    };
  };

  const headerProps = useMemo(() => sessionState, [sessionState]);

  const handleNavigateBack = () => navigate(-1);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current && Object.keys(session).length === 0) {
      dispatch(fetchSession(sessionId));
    } else {
      const headerInfo = getSessionHeaderInfo(session, finished);
      setSessionState(headerInfo);
    }
    return () => (mounted.current = false);
  }, [session, finished, sessionId]);

  // useEffect(() => {
  //   mounted.current = true;
  //   if (error) return;
  //   if (mounted.current) {
  //     dispatch(fetchResultsBySession(sessionId));
  //     dispatch(fetchAnswersBySession(sessionId));
  //   }
  //   return () => (mounted.current = false);
  // }, [answers.length, questions, error, sessionId]);

  useEffect(() => {
    mounted.current = true;
    if (error) return;
    if (mounted.current) {
      dispatch(fetchResultsBySession(sessionId));
      dispatch(fetchAnswersBySession(sessionId));
    }
    return () => (mounted.current = false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current && onlyWrongQuestions.length > 0) {
      setIsLoading(false);
    }
    return () => (mounted.current = false);
  }, [onlyWrongQuestions]);

  if (isLoading) {
    return (
      <section className="h-screen flex items-center justify-center">
        <Loading />
      </section>
    );
  }

  return (
    <Container className="pt-4 position-relative pb-24">
      <Header {...headerProps} />
      <div className="flex items-center justify-between mb-4">
        <p className="font-bold text-xl mb-2">
          You answered {onlyWrongQuestions?.length} out of {questions?.length}{" "}
          questions incorrectly.
        </p>

        <div className="border-1 border-black rounded-full">
          <IconButton
            aria-label="Navigate Home"
            color="inherit"
            onClick={handleNavigateBack}
          >
            <WestIcon />
          </IconButton>
        </div>
      </div>
      <div className="box p-12 py-8 mb-2">
        {onlyWrongQuestions.map((question, index) => {
          return (
            <div key={index} className="py-2">
              {question.questions &&
                question.questions.map((question, i) => {
                  return (
                    <div key={i} className="py-1 mb-4">
                      <p className="text-xl font-bold m-2">
                        {`${index + 1}.`} {decode(question.question)}
                      </p>
                      {
                        <ol
                          className={
                            "list-group d-flex flex-row flex-wrap row p-3"
                          }
                        >
                          {/* Get the options, render the text and show which option the candidate has selected */}
                          {question.options &&
                            question.options.map((option, i) => {
                              const questionType = question.qtype.typename;
                              const currSelectedOption = answers.filter(
                                (answer) => answer.qid === question.qid,
                              )[0]?.answercontent[questionType];
                              let isChecked = currSelectedOption
                                ? questionType === "singlechoice"
                                  ? currSelectedOption === option.qoid
                                  : currSelectedOption.includes(option.qoid)
                                : false;

                              let isCorrect = option.iscorrect;

                              return (
                                <li
                                  key={i}
                                  className={`${
                                    isChecked
                                      ? isCorrect
                                        ? "bg-[#86bc1956]"
                                        : "bg-[#95343256]"
                                      : isCorrect
                                        ? "bg-[#95343256]"
                                        : ""
                                  } list-group-item flex justify-between !opacity-100`}
                                >
                                  <Form.Check
                                    type={
                                      questionType === "singlechoice"
                                        ? "radio"
                                        : "checkbox"
                                    }
                                    label={decode(option.opttext)}
                                    disabled
                                    aria-label={option.opttext || "Checkbox"}
                                    name={`singleAnswer_${question.qid}`}
                                    value={option.qoid}
                                    defaultChecked={isChecked}
                                    className="text-black !opacity-100"
                                  />
                                  {isChecked ? (
                                    isCorrect ? (
                                      <CheckIcon className="text-[#00693C]" />
                                    ) : (
                                      <CloseIcon className="text-[#953432]" />
                                    )
                                  ) : isCorrect ? (
                                    <CloseIcon className="text-[#953432]" />
                                  ) : null}
                                </li>
                              );
                            })}
                        </ol>
                      }
                      <div className="bg-[#326295] text-white list-group d-flex flex-row flex-wrap row m-1 mt-2">
                        <div className="p-3">
                          <p className="font-semibold">You answered:</p>
                          <ol className="font-light">
                            {question.options
                              .filter((option) => {
                                const questionType = question.qtype.typename;
                                const optionSelected = answers.find(
                                  (answer) => answer.qid === question.qid,
                                )?.answercontent[questionType];

                                return optionSelected
                                  ? questionType === "singlechoice"
                                    ? optionSelected === option.qoid
                                    : optionSelected.includes(option.qoid)
                                  : false;
                              })
                              .map((option, i) => {
                                return (
                                  <li key={i}>{decode(option.opttext)}</li>
                                );
                              })}
                          </ol>
                        </div>
                        <div className="">
                          <hr />
                        </div>
                        <div className="p-3">
                          <p className="font-semibold">
                            {question.options.filter((option) => {
                              return option.iscorrect;
                            }).length > 1
                              ? "The correct answers are:"
                              : "The correct answer is:"}
                          </p>
                          <ol className="font-bold">
                            {question.options
                              .filter((option) => {
                                return option.iscorrect;
                              })
                              .map((option, i) => [
                                <li key={i} className="font-light">
                                  {decode(option.opttext)}
                                </li>,
                              ])}
                          </ol>
                        </div>
                        {question.justification && (
                          <>
                            <div className="">
                              <hr />
                            </div>
                            <div className="p-3">
                              <p className="font-semibold">Justification:</p>
                              <p className="font-light">
                                {question.justification}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </Container>
  );
}
